import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';
import { authStorage } from '../utils/storage';
import ProfileCompletionModal from './shared/ProfileCompletionModal';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_yachangu-pay/artifacts/ynseryay_EE6C3FEA-5C5E-424A-B08C-ED1F2A53B790.jpeg';

const Login = ({ onAuthSuccess, setUserData, setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingProfile, setPendingProfile] = useState(null);

  const proceedAfterAuth = async (data) => {
    // Always persist the session — users stay logged in until they log out from settings.
    await authStorage.saveAuth({ token: data.token, user: data.user }, true);
    const user = data.user;
    if (setUserData) setUserData(user);
    if (setIsAuthenticated) setIsAuthenticated(true);
    if (onAuthSuccess) onAuthSuccess(user);
    navigate(user.role === 'driver' ? '/driver/dashboard' : '/rider/dashboard');
  };

  const finishAuth = async (data) => {
    // Social sign-ups (Google/Apple) must complete their real ID name before proceeding.
    const provider = data?.user?.auth_provider;
    if ((provider === 'google' || provider === 'apple') && !data?.user?.profile_completed) {
      setPendingProfile(data);
      return;
    }
    await proceedAfterAuth(data);
  };

  // Handle the Google OAuth return (session_id arrives in the URL hash)
  useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.includes('session_id=')) return;
    const sessionId = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');
    if (!sessionId) return;
    // Clean the hash so a refresh doesn't re-trigger it
    window.history.replaceState(null, '', window.location.pathname);
    setGoogleLoading(true);
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/auth/google/session`, { session_id: sessionId });
        if (res.data?.success && res.data?.token) {
          await finishAuth(res.data);
        } else {
          setError('Google sign-in failed. Please try again.');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.identifier || !formData.password) {
      setError('Please enter your email/phone and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/auth/login`, formData);
      if (typeof response.data !== 'object' || response.data === null) {
        setError('Could not reach the server. Please check your connection and try again.');
        return;
      }
      if (response.data.success) {
        await finishAuth(response.data);
      } else {
        setError(response.data.message || 'Invalid credentials');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid email/phone or password');
      } else if (err.response?.status === 403) {
        setError(err.response.data.detail || 'Account is suspended or deactivated');
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-[100svh] flex flex-col items-center justify-center px-5 py-4 relative overflow-y-auto"
      style={{
        background:
          'radial-gradient(120% 90% at 50% -10%, #1f7a3d 0%, #0d5327 34%, #063217 62%, #02160c 100%)',
      }}
      data-testid="login-page"
    >
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-24 right-[-60px] w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-80px] left-[-40px] w-80 h-80 rounded-full bg-emerald-600/20 blur-3xl" />

      <div className="w-full max-w-md relative">
        {/* Floating logo */}
        <div className="flex justify-center mb-3">
          <div className="w-14 h-14 rounded-[18px] overflow-hidden shadow-2xl ring-1 ring-white/20 bg-white/10 backdrop-blur-md">
            <img src={LOGO_URL} alt="Yachangu" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Glass card */}
        <div
          className="rounded-[28px] border border-white/10 px-6 py-6 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
          style={{ background: 'rgba(9, 46, 26, 0.45)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)' }}
          data-testid="login-card"
        >
          <div className="text-center mb-5">
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Ride Yachangu</h1>
            <p className="text-emerald-100/70 mt-1 text-sm">Sign in to continue your journey.</p>
          </div>

          {(googleLoading) && (
            <div className="mb-5 flex items-center justify-center gap-2 text-emerald-100 text-sm">
              <div className="w-4 h-4 border-2 border-emerald-200 border-t-transparent rounded-full animate-spin" />
              Signing you in with Google…
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/15 border border-red-400/30 rounded-2xl p-3.5 flex items-start gap-2.5" data-testid="login-error">
                <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-100">{error}</p>
              </div>
            )}

            {/* Email / Username */}
            <div>
              <label className="block text-sm font-semibold text-emerald-50/90 mb-2 ml-1">Email or Phone Number</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-200/60" />
                <input
                  type="text"
                  name="identifier"
                  value={formData.identifier}
                  onChange={handleChange}
                  placeholder="Email or phone number"
                  autoComplete="username"
                  data-testid="login-identifier-input"
                  className="w-full h-12 pl-12 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-emerald-100/35 outline-none focus:border-emerald-400/60 focus:bg-white/10 transition-all"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-emerald-50/90 mb-2 ml-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-200/60" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className="w-full h-12 pl-12 pr-12 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-emerald-100/35 outline-none focus:border-emerald-400/60 focus:bg-white/10 transition-all"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="login-toggle-password"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-200/60 hover:text-emerald-100 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                data-testid="login-forgot-password"
                className="text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                Forgot Password?
              </button>
            </div>

            {/* Login button */}
            <button
              type="submit"
              disabled={loading || googleLoading}
              data-testid="login-submit-button"
              className="w-full h-12 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-lg font-bold shadow-lg shadow-emerald-500/30 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
                  Logging in…
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-emerald-100/50 font-medium">Or continue with</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Social buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              disabled
              data-testid="login-apple-button"
              title="Coming soon"
              className="h-12 rounded-2xl bg-white/5 border border-white/10 text-emerald-50/50 font-semibold flex items-center justify-center gap-2 cursor-not-allowed"
            >
              <AppleIcon />
              Apple
            </button>
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="login-google-button"
              className="h-12 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/15 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              <GoogleIcon />
              Google
            </button>
          </div>

          {/* Create account */}
          <p className="text-center text-emerald-50/80 mt-5 text-sm">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="login-create-account"
              className="font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Create Account
            </button>
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 mt-4 text-emerald-100/35">
          <ShieldCheck className="w-4 h-4" />
          <span className="text-xs font-semibold tracking-[0.15em]">SECURED BY YACHANGU</span>
        </div>
      </div>

      {pendingProfile && (
        <ProfileCompletionModal
          auth={pendingProfile}
          onComplete={(data) => { setPendingProfile(null); proceedAfterAuth(data); }}
        />
      )}
    </div>
  );
};

const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41.2 36.6 44 30.8 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

export default Login;
